/**
 * post-apply-single-ask — guest tab-return alert ask.
 *
 * PostHog, proven-human cohort, 2026-10-14..10-30: the "did you apply?" toast
 * on tab return reaches ~47 human guests per 16 days, ~6 answer "applied", so
 * the email ask behind "yes" barely runs. The test arm turns that toast into
 * ONE card: "Mau loker {topic} baru tiap pagi?" + email (MagicLinkForm), with
 * the answer buttons kept as a secondary row.
 *
 * The alert reuses the existing guest pipeline (see jobdetail-alert-capture):
 * the magic link IS the double opt-in, and its callbackURL carries
 * `alert_capture=post_apply` + the filter, so the return trip (any locale page;
 * `PostApplyAlertReturn` is mounted with the layout-level toast) calls
 * `POST /api/saved-searches` with `source: "post_apply"`.
 *
 * Flag `post-apply-single-ask` (boolean) is the SOLE switch in production.
 * Plan: docs/plans/post-apply-single-ask-2026-10-30.md
 */

import type { SavedSearchFilters } from "@/lib/db/queries/saved-searches";
import { internalJobId } from "@/lib/jobs/public-id";
import { ALERT_CAPTURE_PARAM } from "./jobdetail-alert-capture";

export { PREVIEW_FORCE_COOKIE } from "@/lib/flags/preview-force";

export const POST_APPLY_ASK_FLAG = "post-apply-single-ask";
/** `source` on the return-trip `POST /api/saved-searches` (`alert_created`). */
export const POST_APPLY_ALERT_SOURCE = "post_apply";
export { ALERT_CAPTURE_PARAM };
/** Return-trip filter params, read client-side only. */
export const ALERT_Q_PARAM = "alert_q";
export const ALERT_CAT_PARAM = "alert_cat";

export const LAST_SEARCH_STORAGE_KEY = "ld_last_search_q";
export const JOB_CATEGORY_STORAGE_KEY = "ld_job_category_v1";
/** Rendered topic cap (chars, before the ellipsis). */
export const MAX_TOPIC_CHARS = 40;
/** saved-search filter strings are capped at 80 by `sanitizeFilters`. */
const MAX_FILTER_CHARS = 80;

export type AskArm = "test" | "control";
export type TopicSource = "search" | "category" | "generic";

export type AskTopic = {
	source: TopicSource;
	/** Display label, already trimmed and capped; `null` for generic. */
	label: string | null;
	/** Filters the return trip saves. `{}` = all jobs (generic). */
	filters: SavedSearchFilters;
};

function collapse(raw: string): string {
	return raw.replace(/\s+/g, " ").trim();
}

/** Trim, collapse whitespace, cap at MAX_TOPIC_CHARS (+ "…"). Text only. */
export function cleanTopic(raw: string | null | undefined): string | null {
	if (typeof raw !== "string") return null;
	const v = collapse(raw);
	if (!v) return null;
	if (v.length <= MAX_TOPIC_CHARS) return v;
	return `${v.slice(0, MAX_TOPIC_CHARS).trimEnd()}…`;
}

/** Search query > job category > generic. */
export function pickAskTopic(input: {
	searchQuery: string | null;
	category: { slug: string; name: string | null } | null;
}): AskTopic {
	const q = input.searchQuery ? collapse(input.searchQuery) : "";
	if (q && q.length <= MAX_FILTER_CHARS) {
		return { source: "search", label: cleanTopic(q), filters: { q } };
	}
	const cat = input.category;
	if (cat?.slug && cat.slug.length <= MAX_FILTER_CHARS) {
		return {
			source: "category",
			label: cleanTopic(cat.name) ?? cleanTopic(cat.slug),
			filters: { category: cat.slug },
		};
	}
	return { source: "generic", label: null, filters: {} };
}

/** Called next to every `search_submitted` capture. Never throws. */
export function rememberLastSearchQuery(query: string): void {
	const q = collapse(query);
	if (!q) return;
	try {
		window.sessionStorage.setItem(LAST_SEARCH_STORAGE_KEY, q.slice(0, 200));
	} catch {
		// Storage blocked — the ask falls back to the category/generic topic.
	}
}

export function readLastSearchQuery(): string | null {
	try {
		const v = window.sessionStorage.getItem(LAST_SEARCH_STORAGE_KEY);
		return v ? collapse(v) || null : null;
	} catch {
		return null;
	}
}

/** Job detail page memo: lets the layout-level toast name the category. */
export function rememberJobCategory(
	jobId: string,
	slug: string | null,
	name: string | null,
): void {
	if (!slug) return;
	try {
		window.sessionStorage.setItem(
			JOB_CATEGORY_STORAGE_KEY,
			JSON.stringify({ jobId: internalJobId(jobId), slug, name }),
		);
	} catch {
		// Storage blocked — generic topic.
	}
}

export function readJobCategory(
	jobId: string,
): { slug: string; name: string | null } | null {
	try {
		const raw = window.sessionStorage.getItem(JOB_CATEGORY_STORAGE_KEY);
		if (!raw) return null;
		const v = JSON.parse(raw) as Record<string, unknown>;
		if (v.jobId !== internalJobId(jobId)) return null;
		if (typeof v.slug !== "string" || !v.slug) return null;
		return {
			slug: v.slug,
			name: typeof v.name === "string" && v.name ? v.name : null,
		};
	} catch {
		return null;
	}
}

/**
 * Magic-link callbackURL: the page the guest is on, tagged for the return
 * trip. Relative (path + query) so it stays same-origin.
 */
export function buildPostApplyCallbackURL(
	currentHref: string,
	filters: SavedSearchFilters,
): string {
	const url = new URL(currentHref);
	url.searchParams.delete(ALERT_Q_PARAM);
	url.searchParams.delete(ALERT_CAT_PARAM);
	url.searchParams.set(ALERT_CAPTURE_PARAM, POST_APPLY_ALERT_SOURCE);
	if (filters.q) url.searchParams.set(ALERT_Q_PARAM, filters.q);
	else if (filters.category)
		url.searchParams.set(ALERT_CAT_PARAM, filters.category);
	return `${url.pathname}${url.search}`;
}

/** Return-trip filters from the landing URL's query, or null if not ours. */
export function parsePostApplyReturn(
	search: string,
): SavedSearchFilters | null {
	const params = new URLSearchParams(search);
	if (params.get(ALERT_CAPTURE_PARAM) !== POST_APPLY_ALERT_SOURCE) return null;
	const q = collapse(params.get(ALERT_Q_PARAM) ?? "");
	if (q && q.length <= MAX_FILTER_CHARS) return { q };
	const category = (params.get(ALERT_CAT_PARAM) ?? "").trim();
	if (category && category.length <= MAX_FILTER_CHARS) return { category };
	return {};
}

/** URL with the return-trip params removed (path + query + hash). */
export function stripPostApplyReturnParams(href: string): string {
	const url = new URL(href);
	url.searchParams.delete(ALERT_CAPTURE_PARAM);
	url.searchParams.delete(ALERT_Q_PARAM);
	url.searchParams.delete(ALERT_CAT_PARAM);
	return `${url.pathname}${url.search}${url.hash}`;
}

/**
 * Hosts where the e2e force cookie may pick an arm. Closed positive set:
 * production (lokerdollar.com and every subdomain) is never in it, so PostHog
 * stays the sole switch there. E2E lanes run on localhost (local `next dev`)
 * or an ephemeral `*.workers.dev` preview worker, neither of which ever
 * initialises client PostHog, so without this no lane could render the test
 * arm (2026-10-27: the same hole let apply-cap-wall ship untested).
 */
export function isForceableHost(hostname: string): boolean {
	return (
		hostname === "localhost" ||
		hostname === "127.0.0.1" ||
		hostname.endsWith(".workers.dev")
	);
}

/**
 * Arm forced by the non-production `ld_preview_force` cookie
 * (`post-apply-single-ask=test|control`), or null. Same token syntax as
 * src/lib/flags/preview-force.ts.
 */
export function forcedAskArm(
	cookieValue: string | null | undefined,
	hostname: string,
): AskArm | null {
	if (!isForceableHost(hostname) || !cookieValue) return null;
	let decoded = cookieValue;
	try {
		decoded = decodeURIComponent(cookieValue);
	} catch {
		// Use the raw value.
	}
	for (const token of decoded.split(",")) {
		const sep = token.indexOf("=");
		if (sep <= 0) continue;
		if (token.slice(0, sep).trim() !== POST_APPLY_ASK_FLAG) continue;
		const v = token.slice(sep + 1).trim();
		return v === "test" || v === "control" ? v : null;
	}
	return null;
}

/**
 * Mutual exclusion with the `aha-signup-ask` experiment (#7600), which asks
 * the same anonymous appliers to sign up at their first job-detail apply
 * click. Owner decision 2026-10-30: run both, never on the same guest.
 *
 * - aha control arm: excluded here, so the aha control stays "no ask".
 * - aha card already seen: excluded here, so nobody gets both cards.
 */
export function isExcludedByAhaSignupAsk(input: {
	ahaArm: "control" | "treatment" | null;
	sawAhaCard: boolean;
}): boolean {
	return input.ahaArm === "control" || input.sawAhaCard;
}
